import React from 'react'
import Link from 'next/link'
import { useTranslation } from '@/localization/useTranslation'
import type { TranslationType } from '@/types/common'

/**
 * The "Terms and Conditions" link, shared by the auth register checkbox and the
 * checkout payment footer. Only the link is shared — each caller keeps its own
 * checkbox markup and lead-in copy ("I have read and accepted the" vs "I agree
 * to the"), which differ.
 *
 * The URL is per-app and comes from the cinematic, so there is nothing to fall
 * back to when it is absent: the label renders as plain text rather than a link
 * to nowhere. Two shapes made that necessary, and both used to reach `<Link>`:
 *
 * - no URL configured, or an empty one — `t('')` returns `''`, which renders
 *   `<a href="">` and reopens the page the user is already on
 * - a URL translated for only some languages — `t()` returns `undefined` when the
 *   object carries neither the active language nor `original` nor `en`, and
 *   `<Link href={undefined}>` throws, taking the surrounding form down with it
 */
export const TermsAndConditionsLink = ({ href }: { href?: TranslationType }) => {
  const { t } = useTranslation()
  const url = href && t(href)
  const label = t('Terms and Conditions')

  if (!url) return <>{label}</>

  return (
    <Link href={url} target="_blank" rel="noopener noreferrer">
      {label}
    </Link>
  )
}
